import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Zap, Building2, Clock } from "lucide-react";

export function useOutages() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/outages").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 300_000);
    return () => clearInterval(t);
  }, []);
  return data;
}

const fmt = (n) => (n ?? 0).toLocaleString("en-US");
const asOf = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" }) + " ET";
};

export function OutagesSlide({ outages }) {
  const counties = outages?.counties || [];
  if (!outages?.total_out) return null;
  const max = Math.max(...counties.map((c) => c.out), 1);
  return (
    <div data-testid="outages-slide" className="relative h-full w-full overflow-hidden" style={{ background: "linear-gradient(135deg, #0b1322 0%, #0a0f1c 60%, #10060a 100%)" }}>
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "repeating-linear-gradient(135deg, #fff 0 2px, transparent 2px 26px)" }} />
      <div className="relative grid h-full w-full grid-cols-[40%_1fr] gap-7 px-[52px] py-[34px]">
        {/* Statewide headline */}
        <div className="flex flex-col justify-center">
          <div className="flex items-center gap-2.5 font-oswald text-[18px] font-bold uppercase tracking-[0.26em] text-[#ffd60a]">
            <Zap className="h-6 w-6" strokeWidth={2.5} /> Georgia Power Outages
          </div>
          <div className="mt-1 font-oswald text-[136px] font-black leading-[0.82] text-white" data-testid="outages-total">{fmt(outages.total_out)}</div>
          <div className="font-oswald text-[26px] font-bold uppercase tracking-wide text-[#c3ccd8]">Customers without power</div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="rounded-[4px] bg-white/10 px-4 py-2 font-oswald text-[24px] font-black uppercase tracking-wide text-white" data-testid="outages-county-count">{outages.county_count} {outages.county_count === 1 ? "County" : "Counties"}</span>
            {outages.top_utility && (
              <span className="flex items-center gap-2 rounded-[4px] bg-white/10 px-4 py-2 font-oswald text-[22px] font-bold uppercase tracking-wide text-[#c3ccd8]">
                <Building2 className="h-5 w-5" /> {outages.top_utility}
              </span>
            )}
          </div>
          <div className="mt-6 flex items-center gap-2 font-oswald text-[19px] font-semibold uppercase tracking-[0.18em] text-[#7e8aa0]">
            <Clock className="h-5 w-5" /> As of {asOf(outages.updated)} · {outages.source}
          </div>
        </div>

        {/* Hardest-hit counties */}
        <div className="flex flex-col rounded-[10px] bg-white/[0.04] p-6 ring-1 ring-white/10">
          <div className="mb-3 font-oswald text-[25px] font-black uppercase tracking-wider text-white">Hardest-Hit Counties</div>
          <div className="flex min-h-0 flex-1 flex-col justify-between gap-1.5">
            {counties.map((c, i) => (
              <div key={c.fips || c.name} className="flex items-center gap-4" data-testid={`outage-county-${i}`}>
                <div className="w-[34px] shrink-0 font-oswald text-[22px] font-black text-[#5b6675]">{i + 1}</div>
                <div className="w-[230px] shrink-0">
                  <div className="font-oswald text-[26px] font-bold uppercase leading-none text-white">{c.name}</div>
                  {c.utilities?.length ? <div className="mt-0.5 truncate font-oswald text-[18px] font-semibold uppercase tracking-wide text-[#7e8aa0]">{c.utilities[0]}</div> : null}
                </div>
                <div className="relative h-[20px] flex-1 overflow-hidden rounded-full bg-white/10">
                  <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${Math.max(6, (c.out / max) * 100)}%`, background: "linear-gradient(90deg, #f6a21c, #d90429)" }} />
                </div>
                <div className="w-[108px] shrink-0 text-right font-oswald text-[26px] font-black tabular-nums text-[#ffd60a]">{fmt(c.out)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
